import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { spaLogin as apiSpaLogin } from '../api/auth';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styles from './OrganizerRegisterPage.module.css';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../config/mapTile';
import pinIconUrl from '../assets/pin.svg';

const venuePinIcon = L.icon({
  iconUrl: pinIconUrl,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

const VenueMarker = ({ setLatLng }) => {
  useMapEvents({
    click(e) {
      setLatLng(e.latlng);
    },
  });
  return null;
};

const OrganizerRegisterPage = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    role: '',
    venue_name: '',
    address: '',
    description: '',
    organization_name: '',
    slug: '',
    // website削除
  });

  const [latLng, setLatLng] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [agreedDocIds, setAgreedDocIds] = useState({});
  const [viewedDocIds, setViewedDocIds] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);
  const [loadingRequiredDocs, setLoadingRequiredDocs] = useState(true);
  // チャージバック・返金費用同意チェックは不要

  useEffect(() => {
    const fetchRequiredDocs = async () => {
      setLoadingRequiredDocs(true);
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/terms/required`, {
          params: { role: 'seller' },
        });
        const docs = Array.isArray(res.data?.documents) ? res.data.documents : [];
        setRequiredDocs(docs);
      } catch (error) {
        setFieldErrors((prev) => ({
          ...prev,
          agreements: ['同意対象の規約情報を取得できませんでした。時間をおいて再度お試しください。'],
        }));
      } finally {
        setLoadingRequiredDocs(false);
      }
    };

    fetchRequiredDocs();
  }, []);

  const getFieldError = (name) => fieldErrors?.[name]?.[0] || '';

  const clearFieldError = (name) => {
    setFieldErrors((prev) => {
      if (!prev?.[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const buildRegisterError = (err) => {
    const data = err?.response?.data;
    const normalizedFieldErrors = {};

    if (data?.errors && typeof data.errors === 'object') {
      Object.entries(data.errors).forEach(([key, value]) => {
        const messages = Array.isArray(value) ? value.map((msg) => String(msg)) : [String(value)];
        normalizedFieldErrors[key] = messages;
      });
    }

    if (data?.errors && typeof data.errors === 'object') {
      const messages = Object.values(data.errors).flat().map((msg) => String(msg));
      if (messages.length > 0) {
        return {
          message: Array.from(new Set(messages)).join(' / '),
          fieldErrors: normalizedFieldErrors,
        };
      }
    }

    if (data?.message) {
      return {
        message: String(data.message),
        fieldErrors: normalizedFieldErrors,
      };
    }

    return {
      message: '登録に失敗しました。入力内容を確認してください。',
      fieldErrors: normalizedFieldErrors,
    };
  };

  const validateAndCheckSlug = async (rawSlug) => {
    const slug = rawSlug.trim().toLowerCase();
    if (!slug) return;

    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
      setFieldErrors((prev) => ({
        ...prev,
        slug: ['スラッグは小文字の英数字とハイフンのみ使用できます。'],
      }));
      return;
    }

    clearFieldError('slug');

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/check-slug`, { params: { slug } });
      if (res.data.exists) {
        setFieldErrors((prev) => ({
          ...prev,
          slug: ['そのスラッグは既に使用されています。別の文字列を指定してください。'],
        }));
      }
    } catch (err) {
      // ignore network errors for now
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'role') {
      setForm((prev) => ({
        ...prev,
        role: value,
        venue_name: '',
        address: '',
        description: '',
        organization_name: '',
        slug: '',
      }));
      setLatLng(null);
      setFieldErrors({});
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
    clearFieldError(name);

    if (name === 'slug') {
      clearFieldError('slug');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});

    if (loadingRequiredDocs) {
      setFieldErrors({ agreements: ['同意対象の規約情報を読み込み中です。しばらくお待ちください。'] });
      return;
    }

    if (requiredDocs.length === 0) {
      setFieldErrors({ agreements: ['同意対象の規約情報を取得できませんでした。ページを再読み込みしてください。'] });
      return;
    }

    if (!form.role) {
      setFieldErrors({ role: ['種別を選択してください。'] });
      return;
    }

    // 入力バリデーション
    if (form.role === 'artist' && !form.organization_name.trim()) {
      setFieldErrors({ organization_name: ['アーティスト名を入力してください。'] });
      return;
    }

    if (form.role === 'venue' && !latLng) {
      setFieldErrors({ latitude: ['地図上で会場の位置を選択してください。'] });
      return;
    }

    const missingDoc = requiredDocs.find((doc) => !agreedDocIds[doc.id]);
    if (missingDoc) {
      setFieldErrors({ agreements: ['必須規約への同意が必要です。'] });
      return;
    }

    const data = {
      ...form,
      latitude: latLng?.lat,
      longitude: latLng?.lng,
      agreements: requiredDocs.map((doc) => ({ terms_version_id: doc.id })),
    };

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/seller-register`, data);

      let resendVerificationToken = '';

      try {
        await apiSpaLogin({ email: form.email, password: form.password });
      } catch (loginError) {
        const responseData = loginError?.response?.data;
        if (responseData?.error_code === 'email_unverified') {
          resendVerificationToken = responseData?.resend_verification_token || '';
        }
      }

      navigate('/email-verification-notice', {
        state: {
          email: form.email,
          resendVerificationToken,
        },
      });
    } catch (err) {
      console.error('登録エラー:', err);
      const parsed = buildRegisterError(err);
      setFieldErrors(parsed.fieldErrors);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>チケット販売者 登録</h2>

      <form onSubmit={handleSubmit} className={styles.form}>
        <label htmlFor="name">名前</label>
        <input id="name" type="text" name="name" placeholder="名前（代表者）※本名" value={form.name} onChange={handleChange} className={getFieldError('name') ? styles.inputError : ''} required />
        {getFieldError('name') && <p className={styles.fieldError}>{getFieldError('name')}</p>}
        <div style={{ height: 8 }} />
        <label htmlFor="email">メール</label>
        <input id="email" type="email" name="email" placeholder="email@example.com" autoComplete="username" value={form.email} onChange={handleChange} className={getFieldError('email') ? styles.inputError : ''} required />
        {getFieldError('email') && <p className={styles.fieldError}>{getFieldError('email')}</p>}
        <div style={{ height: 8 }} />
        <label htmlFor="password">パスワード</label>
        <input id="password" type="password" name="password" placeholder="大文字小文字数字記号を含む8文字以上" autoComplete="new-password" value={form.password} onChange={handleChange} className={getFieldError('password') ? styles.inputError : ''} required />
        {getFieldError('password') && <p className={styles.fieldError}>{getFieldError('password')}</p>}
        <div style={{ height: 8 }} />
        <input id="password_confirmation" type="password" name="password_confirmation" placeholder="パスワード確認" autoComplete="new-password" value={form.password_confirmation} onChange={handleChange} className={getFieldError('password_confirmation') ? styles.inputError : ''} required />
        {getFieldError('password_confirmation') && <p className={styles.fieldError}>{getFieldError('password_confirmation')}</p>}
        <div style={{ height: 8 }} />

        <label htmlFor="role" style={{ fontWeight: 'bold', marginBottom: 4 }}>種別の選択</label>
        <div style={{ marginBottom: 8 }}>
          <select id="role" name="role" value={form.role} onChange={handleChange} className={getFieldError('role') ? styles.inputError : ''} required>
            <option value="">選択してください</option>
            <option value="organizer">主催者・イベント団体</option>
            <option value="artist">アーティスト</option>
            <option value="venue">会場</option>
          </select>
          {getFieldError('role') && <p className={styles.fieldError}>{getFieldError('role')}</p>}
        </div>

        {form.role === 'organizer' && (
          <>
            <label htmlFor="organization_name_organizer">イベント団体名（活動名）</label>
            <input
              id="organization_name_organizer"
              type="text"
              name="organization_name"
              placeholder="イベント団体名（活動名）"
              value={form.organization_name}
              onChange={handleChange}
              className={getFieldError('organization_name') ? styles.inputError : ''}
              required
            />
            {getFieldError('organization_name') && <p className={styles.fieldError}>{getFieldError('organization_name')}</p>}
            <div style={{ height: 8 }} />
            <div style={{ marginBottom: 8 }}>
              <label htmlFor="slug_organizer" style={{ fontWeight: 'bold' }}>公開用URL（スラッグ）</label>
              <input
                id="slug_organizer"
                type="text"
                name="slug"
                placeholder="例: acme-live（半角英数字とハイフン）"
                value={form.slug}
                onChange={handleChange}
                onBlur={(e) => validateAndCheckSlug(e.target.value)}
                className={getFieldError('slug') ? styles.inputError : ''}
                required
              />
              {getFieldError('slug') && <p className={styles.fieldError}>{getFieldError('slug')}</p>}
              <div style={{ fontSize: 12, color: '#666' }}>プロフィールの末尾に表示されます。例: <code>{`${process.env.REACT_APP_SITE_URL}/profile/acme-live`}</code></div>
            </div>
            <div style={{ height: 8 }} />
          </>
        )}

        {form.role === 'artist' && (
          <>
            <label htmlFor="organization_name_artist">アーティスト名</label>
            <input
              id="organization_name_artist"
              type="text"
              name="organization_name"
              placeholder="アーティスト名"
              value={form.organization_name}
              onChange={handleChange}
              className={getFieldError('organization_name') ? styles.inputError : ''}
              required
            />
            {getFieldError('organization_name') && <p className={styles.fieldError}>{getFieldError('organization_name')}</p>}
            <div style={{ height: 8 }} />
            <div style={{ marginBottom: 8 }}>
              <label htmlFor="slug_artist" style={{ fontWeight: 'bold' }}>公開用URL（スラッグ）</label>
              <input
                id="slug_artist"
                type="text"
                name="slug"
                placeholder="例: artist-name（半角英数字とハイフン）"
                value={form.slug}
                onChange={handleChange}
                onBlur={(e) => validateAndCheckSlug(e.target.value)}
                className={getFieldError('slug') ? styles.inputError : ''}
                required
              />
              {getFieldError('slug') && <p className={styles.fieldError}>{getFieldError('slug')}</p>}
              <div style={{ fontSize: 12, color: '#666' }}>プロフィールの末尾に表示されます。登録後は変更できません。例: <code>{`${process.env.REACT_APP_SITE_URL}/profile/artist-name`}</code></div>
            </div>
            <div style={{ height: 8 }} />
            <div style={{ height: 8 }} />
          </>
        )}

        {form.role === 'venue' && (
          <>
            <div>
              <div style={{ marginBottom: 8 }}>
                <label htmlFor="slug_venue" style={{ fontWeight: 'bold' }}>公開用URL（スラッグ）</label>
                <input
                  id="slug_venue"
                  type="text"
                  name="slug"
                  placeholder="例: venue-name（半角英数字とハイフン）"
                  value={form.slug}
                  onChange={handleChange}
                    onBlur={(e) => validateAndCheckSlug(e.target.value)}
                    className={getFieldError('slug') ? styles.inputError : ''}
                  required
                />
                  {getFieldError('slug') && <p className={styles.fieldError}>{getFieldError('slug')}</p>}
                <div style={{ fontSize: 12, color: '#666' }}>プロフィールの末尾に表示されます。例: <code>{`${process.env.REACT_APP_SITE_URL}/profile/venue-name`}</code></div>
              </div>
              <label htmlFor="venue_name">会場名</label>
                <input id="venue_name" type="text" name="venue_name" placeholder="会場名" value={form.venue_name} onChange={handleChange} className={getFieldError('venue_name') ? styles.inputError : ''} required />
                {getFieldError('venue_name') && <p className={styles.fieldError}>{getFieldError('venue_name')}</p>}
              <div style={{ height: 8 }} />
              <label htmlFor="address">住所</label>
                <input id="address" type="text" name="address" placeholder="住所" value={form.address} onChange={handleChange} className={getFieldError('address') ? styles.inputError : ''} required />
                {getFieldError('address') && <p className={styles.fieldError}>{getFieldError('address')}</p>}
              <div style={{ height: 8 }} />
              <label>会場位置（地図）</label>
              <div style={{ marginBottom: 4, color: '#555', fontSize: 14 }}>地図をクリックして場所を選択</div>
                <div className={getFieldError('latitude') ? styles.mapError : ''}>
                  <MapContainer center={[35.68, 139.76]} zoom={12} style={{ height: '300px', marginBottom: '10px' }}>
                    <TileLayer url={MAP_TILE_URL} attribution={MAP_TILE_ATTRIBUTION} />
                    <VenueMarker
                      setLatLng={(value) => {
                        setLatLng(value);
                        clearFieldError('latitude');
                      }}
                    />
                    {latLng && <Marker position={latLng} icon={venuePinIcon} />}
                  </MapContainer>
                </div>
                {getFieldError('latitude') && <p className={styles.fieldError}>{getFieldError('latitude')}</p>}
            </div>
            <div style={{ height: 8 }} />
          </>
        )}

        {form.role && (
          <>
            <label htmlFor="description" style={{ fontWeight: 'bold', margin: '16px 0 4px 0' }}>説明（任意）</label>
            <textarea
              id="description"
              name="description"
              placeholder="プロフィール説明（任意）"
              value={form.description}
              onChange={handleChange}
              className={getFieldError('description') ? styles.inputError : ''}
            />
            {getFieldError('description') && <p className={styles.fieldError}>{getFieldError('description')}</p>}
            <div style={{ height: 8 }} />
          </>
        )}
        <p style={{ margin: 0, color: '#555', fontSize: 13 }}>
          ※口座情報と本人確認は、登録後にダッシュボードの「販売を開始する」から Stripe Hosted Onboarding でご入力いただきます。
        </p>
        <div style={{ height: 8 }} />

        <div>
          <div style={{ fontWeight: 'bold', marginBottom: 8 }}>同意事項（必須）</div>
          {loadingRequiredDocs && <p style={{ margin: '0 0 8px', color: '#555' }}>規約情報を読み込み中です...</p>}
          {requiredDocs.map((doc) => (
            <label key={doc.id} style={{ display: 'block', marginBottom: 6 }}>
              <input
                type="checkbox"
                checked={!!agreedDocIds[doc.id]}
                disabled={!viewedDocIds[doc.id]}
                onChange={(e) => {
                  setAgreedDocIds((prev) => ({ ...prev, [doc.id]: e.target.checked }));
                  clearFieldError('agreements');
                }}
              />{' '}
              <button
                type="button"
                onClick={() => {
                  setViewedDocIds((prev) => ({ ...prev, [doc.id]: true }));
                  setPreviewDoc(doc);
                }}
                style={{
                  border: 'none',
                  background: 'none',
                  color: 'blue',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: 0,
                  font: 'inherit',
                }}
              >
                {doc.title}
              </button>
              {' '}に同意します
            </label>
          ))}
          {requiredDocs.length > 0 && (
            <p style={{ margin: '4px 0 0', color: '#555', fontSize: 12 }}>※各文書を開くとチェックできるようになります。</p>
          )}
          {getFieldError('agreements') && <p className={styles.fieldError}>{getFieldError('agreements')}</p>}
        </div>
        <div style={{ height: 8 }} />

        <button type="submit" disabled={loadingRequiredDocs}>登録する</button>
      </form>

      {previewDoc && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 1000,
          }}
          onClick={() => setPreviewDoc(null)}
        >
          <div
            style={{
              width: 'min(840px, 100%)',
              maxHeight: '80vh',
              overflow: 'auto',
              background: '#fff',
              borderRadius: 12,
              padding: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 8px' }}>{previewDoc.title}</h3>
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewDoc.content || '') }} />
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
              <button type="button" onClick={() => setPreviewDoc(null)}>閉じる</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizerRegisterPage;
